import { ClaimResponseTypeCode } from '@quromedical/fhir-common';
import type { ClaimResponse } from '@quromedical/healthbridge/client';
import { z } from 'zod';
import { ChargeItemDefinitionMoneySchema } from '../charge-item-definition/schema';
import { DisplayReferenceSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';

export type ClaimResponseModelCreateRequest = ClaimResponse;
export type ClaimResponseModelCreateResponse = fhir4.ClaimResponse;

const ClaimSupportedResponseStatus = [
  'active',
  'cancelled',
  'draft',
  'entered-in-error',
] as const;
const ClaimSupportedResponseOutcome = [
  'queued',
  'complete',
  'error',
  'partial',
] as const;

export const ClaimResponseStatus = z.enum(ClaimSupportedResponseStatus);
export const ClaimResponseOutcome = z.enum(ClaimSupportedResponseOutcome);
export const ClaimTypeSchema = z.nativeEnum(ClaimResponseTypeCode);

export const ClaimResponseItemTotalSchema = z.object({
  billedAmount: ChargeItemDefinitionMoneySchema,
  medicalAidPayable: ChargeItemDefinitionMoneySchema,
  medicalAidRate: ChargeItemDefinitionMoneySchema,
});

export const ClaimResponseTotalSchema = z.object({
  claimedAmount: ChargeItemDefinitionMoneySchema,
  paidProvider: ChargeItemDefinitionMoneySchema,
  balanceOutstanding: ChargeItemDefinitionMoneySchema,
});

export const ClaimResponseItemSchema = z.object({
  sequence: z.number(),
  total: ClaimResponseItemTotalSchema,
  notes: z.string().array(),
});

export const ClaimResponseItemPartialSchema = z.object({
  sequence: z.number(),
  total: ClaimResponseItemTotalSchema.partial(),
  notes: z.string().array(),
});

export const ClaimResponseModelSchema = z.object({
  status: ClaimResponseStatus,
  statusDescription: z.string(),
  outcome: ClaimResponseOutcome,
  type: ClaimTypeSchema,
  patient: DisplayReferenceSchema,
  provider: DisplayReferenceSchema,
  claim: DisplayReferenceSchema,
  date: z.string(),
  items: ClaimResponseItemSchema.array(),
  total: ClaimResponseTotalSchema,
  notes: z.string().array(),
});

export const ClaimResponseModelGetResponseSchema =
  ClaimResponseModelSchema.omit({
    patient: true,
    provider: true,
    claim: true,
    items: true,
    total: true,
  })
    .deepPartial()
    .extend({
      id: z.string(),
      patient: z.optional(DisplayReferenceSchema),
      provider: z.optional(DisplayReferenceSchema),
      claim: z.optional(DisplayReferenceSchema),
      items: ClaimResponseItemPartialSchema.partial().array(),
      total: ClaimResponseTotalSchema.partial(),
    });

export const ClaimResponseModelGetAllResponseSchema =
  PaginatedBundleSchema.extend({
    claimResponses: ClaimResponseModelGetResponseSchema.array(),
  });

export const ClaimResponseGetAllParamsSchema = PaginationSchema.extend({
  outcome: ClaimResponseOutcome.optional(),
  status: ClaimResponseStatus.optional(),
});

export type ClaimResponseModelGetAllResponse = z.infer<
  typeof ClaimResponseModelGetAllResponseSchema
>;

export type ClaimResponseItemTotal = z.infer<
  typeof ClaimResponseItemTotalSchema
>;
export type ClaimResponseTotal = z.infer<typeof ClaimResponseTotalSchema>;

export type ClaimResponseItem = z.infer<typeof ClaimResponseItemSchema>;
export type ClaimResponseModel = z.infer<typeof ClaimResponseModelSchema>;

export type ClaimResponseModelGetResponse = z.infer<
  typeof ClaimResponseModelGetResponseSchema
>;
export type ClaimResponseGetAllParams = z.infer<
  typeof ClaimResponseGetAllParamsSchema
>;
